// ELVA's work — in-memory mock store (read-only). No backend.
import type { LogDay, LogEntry, Period, Report } from './types';
import { allEntries, buildLog, buildReport } from './fixtures';
import { LOG_HISTORY } from './historyMock';
import { csvEscape } from './utils';

/** Curated entries carry "Today/Yesterday" day labels; map them to real dates
 * so the date-range filter can place them (lists at scale, D148). */
const CURATED_ISO: Record<string, string> = { 'Today, Oct 15': '2026-10-15', 'Yesterday, Oct 14': '2026-10-14' };
function datedCurated(e: LogEntry): LogEntry {
  return e.dateIso ? e : { ...e, dateIso: CURATED_ISO[e.day] ?? '2026-10-15' };
}

type Scenario = '' | 'first-week' | 'empty-log';

function readScenario(): Scenario {
  try {
    const raw = new URLSearchParams(window.location.search).get('scenario') ?? '';
    if (raw === 'first-week' || raw === 'empty-log') return raw;
  } catch {
    /* no window */
  }
  return '';
}

export function getReport(period: Period): Report {
  // The period drives every figure, row and section — not just the headline
  // (rcm-03 Fix-on-staging).
  return buildReport(period);
}

export function getLogDays(): LogDay[] {
  const s = readScenario();
  if (s === 'empty-log') return [];
  if (s === 'first-week') return buildLog().filter((d) => d.date.startsWith('Today'));
  return buildLog();
}

/** The whole Log as one flat, newest-first list: curated entries + the big
 * history fixture. The Log pages/searches/filters this client-side (§8). */
export function getLogEntriesFlat(): LogEntry[] {
  const s = readScenario();
  if (s === 'empty-log') return [];
  const curated = allEntries().map(datedCurated);
  if (s === 'first-week') return curated;
  return [...curated, ...LOG_HISTORY];
}

export function getLogEntry(id: string): LogEntry | null {
  return getLogEntriesFlat().find((e) => e.id === id) ?? null;
}

export function getScenario(): Scenario {
  return readScenario();
}

/** Build and download a CSV of the given entries. */
export function downloadLogCsv(entries: LogEntry[]): void {
  const header = ['Day', 'Time', 'Kind', 'What', 'Detail', 'How', 'Result', 'By'];
  const lines = [header.join(',')];
  for (const e of entries) {
    lines.push([e.day, e.time, e.kindLabel, e.what, e.meta, e.how, e.result, e.by].map(csvEscape).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'elva-work-log.csv';
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    /* mock */
  }
}
